/**
 * All functionality needed in News related components.
 *
 * @module feature/Vacancies
 */
import Repository from 'FilterRepository';
import Filter from '../../../../../../Foundation/Tags/code/src/scripts/tags/VacancyFilter.js';
import Pagination from '../../../../../../Foundation/Pagination/code/src/scripts/pagination/Pagination.js';

/**
 * VacanciesSearch filter and pagination functionality.
 *
 * @requires module:project/Common.FilterRepository
 * @requires module:foundation/Tags.VacancyFilter
 * @requires module:foundation/Pagination.Pagination
 * @memberof module:feature/Vacancies
 * @version 1.0.0
 * @author Rocco Janse <rocco.janse@valtech.nl>
 */
class VacanciesSearch {
    /**
     * Upgrades DOM element, adds filter, pagination and repository functionality.
     * @param {DOMElement} element DOM element to upgrade.
     */
    constructor(element) {
        this.$element = $(element);
        this.baseUrl = this.$element.attr('data-base-url');
        this.lang = this.$element.attr('data-lang');
        this.connexysLang = this.$element.attr('data-connexys-lang');
        this.publicationName = this.$element.attr('data-publication-name');
        this.maxResults = this.$element.attr('data-items-per-page');

        this.repository = new Repository({
            apiBase: 'api/feature/vacancies/getvacancies',
            lang: this.lang,
            connexysLang: this.connexysLang,
            publicationName: this.publicationName,
        });
        this.filter = new Filter(this.$element.find('.js-tag-filter'), this.repository).init();
        this.pagination = new Pagination(this.$element.find('.js-pagination'), this.repository, this.maxResults).init();

        this.$loader = this.$element.find('.loader').hide();

        this.$itemCount = this.$element.find('.js-vacancy-count');
        this.$itemList = this.$element.find('.itemlist__container');
        this.itemTemplate = null;

        this.repository.init();
    }

    /**
     * Builds item template and add listeners to repository.
     */
    init() {
        // get item template
        this.itemTemplate = this.buildTemplate(this.$itemList.find('.vacancy-item').first().clone());

        // repository listeners
        this.repository.addListener('beforeInit', () => {
            this.$loader.fadeIn(200);
        });
        this.repository.addListener('beforeUpdate', () => {
            this.$loader.fadeIn(200);
        });
        this.repository.addListener('updated', (pagedata) => {
            this.updateList(pagedata);
        });
    }

    /**
     * Updates list with new items.
     * @param {object} data Json object or resultset to render in list.
     */
    updateList(data) {
        this.$loader.fadeIn(200, () => {
            if (data.Success === false) {
                this.$itemList.html(`<div class="alert alert-danger" role="alert">${data.Reason}</div>`);

                // hide loader
                this.$loader.fadeOut(200);
            } else {
                let htmlArray = [];
                let promises = [];

                // render all items
                for (let item of data.Items) {
                    let promise = new Promise((resolve) => {
                        this.renderItem(item).then((renderedItem) => {
                            htmlArray.push(renderedItem);
                            resolve();
                        });
                    });
                    promises.push(promise);
                }

                // update list when all items rendered
                Promise.all(promises).then(() => {
                    // update DOM
                    this.$itemList.html(htmlArray.join(''));

                    // update total items
                    this.$itemCount.text(data.TotalItems);

                    // update valtagmanager
                    if (window.digitalData) {
                        window.digitalData.update();
                    }

                    // hide loader
                    this.$loader.fadeOut(200);
                });
            }
        });
    }

    /**
     * Finds all variable elements and replaces it with tags
     * so we don't need find queries every time we render a card.
     * @param {jQueryDomElement}  $el Item to create template of.
     * @returns {string} HTML string of item.
     */
    buildTemplate($el) {
        const $wrapper = $('<div></div>').append($el);
        $wrapper.find('.vacancy-item__link').attr('href', '%%ITEMURL%%').prop('style', false);
        $wrapper.find('.js-vac-title').text('%%TITLE%%').attr('href', '%%ITEMURL%%');
        $wrapper.find('.js-vac-subtitle').html('%%SUBTITLE%%');
        $wrapper.find('.js-vac-metalocation').html('%%LOCATION%%');
        $wrapper.find('.js-vac-metaorganisation').html('%%ORGANISATION%%');
        $wrapper.find('.js-vac-metaeducation').html('%%EDUCATION%%');
        return $wrapper.html();
    }

    /**
     * Renders item to template.
     * @param {object} item Json object of item to render.
     */
    renderItem(item) {
        return new Promise((resolve) => {
            let html = this.itemTemplate;
            html = html.replace(/%%ITEMURL%%/g, this.baseUrl + '/' + item.Url || '#');
            html = html.replace(/%%TITLE%%/g, item.Title || '');
            html = html.replace(/%%SUBTITLE%%/g, item.SubTitle || '');
            html = html.replace(/%%LOCATION%%/g, item.Location || '');
            html = html.replace(/%%ORGANISATION%%/g, item.Organisation || '');
            html = html.replace(/%%EDUCATION%%/g, item.Education || '');
            resolve(html);
        });
    }
}

// register to Component Handler
window.ComponentHandler.register({
    constructor: VacanciesSearch,
    classAsString: 'VacanciesSearch',
    cssClass: 'js-vacancies-search',
});

export default VacanciesSearch;
